/**
 * PublicFooter.tsx — WEB-2145 п.3.
 *
 * Тихий подвал публичной страницы: ссылка на наш документ «Данные о здоровье»
 * и значок «передумать о cookie».
 *
 * WEB-2162 п.2 — ПОЧЕМУ ССЫЛКА СТОИТ ВНЕ УСЛОВИЯ ПОКАЗА ЗНАЧКА. Закон штата
 * Вашингтон (My Health My Data) требует ссылку на отдельный документ о данных
 * о здоровье с самой страницы и без оговорок: порогов по размеру компании у
 * закона нет, а иск подаёт сам человек. Значок «передумать» — про cookie, он
 * нужен только тому, кому согласие вообще предлагали (посетитель из ЕС,
 * решение уже принято); ссылка нужна всем и всегда, поэтому ранний выход
 * закрывает теперь только значок, а не весь подвал.
 *
 * Зачем он есть. Системный баннер согласия показывается ОДИН раз: нажал
 * «Принять» или «Отклонить» — и он больше не поднимается (решение записано в
 * куку `w2a_consent`). До этой карточки отозвать согласие было нечем вообще,
 * а право передумать — прямое требование GDPR («отозвать согласие должно быть
 * так же просто, как его дать»).
 *
 * Почему значок, а не ссылка в подвале: у публичной воронки подвала может не
 * быть вовсе — макет `web2app-spec/design/builder-mockup/builder-app.jsx`
 * (строки про `pv-consent-recall`) кладёт значок в угол экрана и делает его
 * нарочно тихим: 20 px, полупрозрачный, без подписи. Он остаётся и ПОСЛЕ
 * «Принять» — иначе согласившийся человек лишался бы возможности передумать,
 * ради которой карточка и заводилась.
 *
 * Пиктограмма — рисунок (SVG), а не эмодзи: эмодзи в интерфейсе мы не ставим
 * даже вопреки макету (правило владельца 12.09).
 *
 * Стили — свои, без Ant Design: компонент живёт на публичной поверхности,
 * которая обязана оставаться лёгкой и не зависеть от темы кабинета (та же
 * причина, что у `components/consent/ConsentBanner.tsx`).
 */
import React from 'react';
import { APP_HOST } from '@/config/public-host';
import { LEGAL_HEALTH_DATA_PATH } from '@/constants/legalPaths';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';

interface PublicFooterProps {
  /** Посетителю показывался (или показался бы) баннер согласия. */
  euVisitor: boolean;
  /** Решение о cookie уже принято — баннера на экране нет. */
  consentDone: boolean;
  /** Поднять баннер согласия заново. */
  onRecall: () => void;
  /** WEB-1820: язык воронки — подпись из словаря publicQuizStrings. */
  language?: string | null;
}

/** Печенье: круг с крошками. Нарисовано контуром текущего цвета. */
function CookieIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        stroke="currentColor"
        strokeWidth="1.3"
        fill="none"
      />
      <circle cx="5.8" cy="6" r="1" fill="currentColor" />
      <circle cx="9.8" cy="5.4" r="0.85" fill="currentColor" />
      <circle cx="10.2" cy="9.6" r="1" fill="currentColor" />
      <circle cx="6.2" cy="10.3" r="0.85" fill="currentColor" />
    </svg>
  );
}

export function PublicFooter({
  euVisitor,
  consentDone,
  onRecall,
  language,
}: PublicFooterProps) {
  const strings = getPublicQuizStrings(language);
  // Условие показа значка — дословно из макета: он появляется у того, кому
  // согласие вообще предлагали, и только когда решение уже принято (пока
  // висит баннер, значок был бы вторым способом сделать то же самое).
  const showRecall = euVisitor && consentDone;

  return (
    <div
      data-testid="pv-consent-footer"
      style={{
        position: 'fixed',
        right: 10,
        bottom: 10,
        // Ниже баннера согласия (2147483000) — когда баннер поднят заново,
        // он обязан перекрывать значок, а не наоборот.
        zIndex: 2147482000,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        justifyContent: 'flex-end',
      }}
    >
      {/* Новая вкладка — не украшение: публичная страница держит ответы квиза
          в памяти, и уход по ссылке в той же вкладке стоил бы человеку
          заполненной воронки. `rel` обязателен вместе с `target`. */}
      <a
        data-testid="pv-health-data-link"
        href={`https://${APP_HOST}${LEGAL_HEALTH_DATA_PATH}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontSize: '11px',
          lineHeight: '20px',
          color: 'currentColor',
          opacity: '0.45',
          textDecoration: 'underline',
        }}
      >
        {strings.healthDataLink}
      </a>
      {showRecall ? (
        <button
          type="button"
          data-testid="pv-consent-recall"
          title={strings.consentRecallLabel}
          aria-label={strings.consentRecallLabel}
          onClick={onRecall}
          style={{
            width: '20px',
            height: '20px',
            padding: 0,
            borderRadius: '50%',
            border: 'none',
            background: 'transparent',
            color: 'currentColor',
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            opacity: '0.35',
          }}
        >
          <CookieIcon />
        </button>
      ) : null}
    </div>
  );
}
